import createSagaMiddleware from 'redux-saga';
import { routerMiddleware } from 'connected-react-router';
import { configureStore } from '@reduxjs/toolkit';

import { sendErrorToSentry, initSentryReduxEnhancer } from 'js/logging';
import { SagaOnlyActions } from 'js/actionCreators/sagaOnlyActions';
import sagas from 'js/sagas';
import reducers from 'js/reducers';

import { getHistory } from './history';

const sagaMiddleware = createSagaMiddleware({
  onError: err => {
    console.error(err);
    sendErrorToSentry(err);
  }
});

const history = getHistory();

const reduxDebuggingEnabled =
  import.meta.env.MODE === 'development' && import.meta.env.VITE_DISABLE_REDUX_DEBUGGING !== 'true';

const serializableCheck = reduxDebuggingEnabled
  ? {
      ignoredActions: [
        'AUTH_SUCCESS',
        'VALIDATE_TOKEN_SUCCESS',
        'UPDATE_BITMAP_FONTS',
        'UPLOAD_RESOURCE_TO_GPU',
        'UPDATE_SCENE_ELEMENTS',
        'EDITOR_UNDO_SUCCESS',
        'EDITOR_REDO_SUCCESS',
        'GET_SCRIBE_TEMPLATES_BY_CATEGORY_SUCCESS',
        'TRACK_TEMPLATE_PREVIEW_CLICK',
        'CREATE_SCRIBE_FROM_TEMPLATE_SUCCESS',
        'PROCESS_IMAGE_FILE_ON_SUCCESSFUL_UPLOAD',
        'REWRITE_HISTORY',
        'SHOW_ERROR'
      ],
      ignoredActionPaths: [
        /scribes\.[0-9]+\.createdOn/,
        /scribes\.[0-9]+\.updatedOn/,
        'scribe',
        'date',
        'element',
        'file',
        'error'
      ],
      ignoredPaths: [
        'auth.currentUser.trialExpiryDate',
        'marketing.personaDismissDate',
        /scribes\.activeScribePast\.[0-9]+/,
        /scribes\.activeScribeFuture\.[0-9]+/,
        /scribes\.homepageProjects\.[0-9]+\.createdOn/,
        /scribes\.homepageProjects\.[0-9]+\.updatedOn/,
        /scribes\.allScribes\.[0-9]+/,
        /autoSave\.pendingSaveQueue\.[0-9]+\.pendingSave/,
        /templates\.templatesByCategory\.[0-9]+\.templates\.[0-9]+\.createdDate/
      ]
    }
  : false;
const thunk = false;
const immutableCheck = reduxDebuggingEnabled;
const actionCreatorCheck = reduxDebuggingEnabled;

export const store = configureStore({
  reducer: reducers(history),
  middleware: getDefaultMiddleware => {
    return (
      getDefaultMiddleware({
        serializableCheck,
        thunk,
        immutableCheck,
        actionCreatorCheck
      })
        .concat(sagaMiddleware)
        .concat(routerMiddleware(history))
    );
  },
  enhancers: getDefaultEnhancers => {
    return getDefaultEnhancers().concat(initSentryReduxEnhancer());
  },
  devTools: {
    serialize: {
      replacer: (key, value) => {
        // Truncate base64 thumbnail image urls to reduce noise/scrolling in redux devtools
        if ((key === 'thumbnailImage' || key === '_imageUrl') && value && typeof value === 'string') {
          return `${value.slice(0, 40)}...`;
        }
        return value;
      }
    }
  }
});

sagaMiddleware.run(sagas);

export function initializeElectronReduxBridge() {
  if (window.electron?.redux?.onDispatch) {
    window.electron.redux.onDispatch(action => {
      store.dispatch(action);
    });
  }
}

export type ReduxRootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch & {
  (action: SagaOnlyActions): void;
};
